import { Injectable } from '@angular/core';
import { CandidateSalary, SalaryLpaFilter } from '../models/candidate-salary.model';

const LAKH = 100_000;
const CRORE = 10_000_000;

const CURRENCY_SYMBOLS: Readonly<Record<string, string>> = {
  USD: '$',
  INR: '₹',
  EUR: '€',
  GBP: '£',
  ILS: '₪'
};

const PERIOD_LABELS: Readonly<Record<string, string>> = {
  annual: 'PA',
  monthly: 'PM',
  hourly: 'PH'
};

const NAUKRI_BAND_FILTERS: Readonly<Record<string, SalaryLpaFilter>> = {
  'less than 5 lpa': '0-10',
  '5 - 10 lpa': '0-10',
  '10 - 20 lpa': '10-20',
  '20 - 30 lpa': '20-30',
  '30 - 40 lpa': '30-40',
  '40+ lpa': '40+'
};

@Injectable({ providedIn: 'root' })
export class SalaryService {
  
  format(salary: CandidateSalary | null | undefined): string | null {
    if (!salary) return null;

    const rawText = this.removeMultipleSpaces(salary.rawText);
    if (rawText && this.getNaukriBandFilter(rawText)) {
      return rawText;
    }

    const amount = this.getPositiveAmount(salary.amount);
    if (amount === null) {
      return rawText;
    }

    const currency = this.removeMultipleSpaces(salary.currency)?.toUpperCase() ?? null;
    const period = this.removeMultipleSpaces(salary.period)?.toLowerCase() ?? null;
    const formattedAmount = currency === 'INR'
      ? this.formatInrAmount(amount)
      : this.formatInternationalAmount(amount);

    return [
      currency ? CURRENCY_SYMBOLS[currency] ?? currency : null,
      formattedAmount,
      period ? PERIOD_LABELS[period] ?? period : null
    ].filter(Boolean).join('');
  }

  isUnverified(salary: CandidateSalary | null | undefined): boolean {
    return salary?.amount == null && !!salary?.rawText?.trim();
  }

  matchesLpaFilters(
    salary: CandidateSalary | null | undefined,
    filters: readonly SalaryLpaFilter[]
  ): boolean {
    if (!filters.length) return true;
    if (!salary) return false;

    const bandFilter = this.getNaukriBandFilter(salary.rawText);
    if (bandFilter) {
      return filters.includes(bandFilter);
    }

    const salaryLpa = this.toAnnualInrLpa(salary);
    return salaryLpa !== null && filters.some(filter => this.isLpaInFilter(salaryLpa, filter));
  }

  private toAnnualInrLpa(salary: CandidateSalary): number | null {
    const amount = this.getPositiveAmount(salary.amount);
    const currency = this.removeMultipleSpaces(salary.currency)?.toUpperCase();
    const period = this.removeMultipleSpaces(salary.period)?.toLowerCase();

    if (amount === null || currency !== 'INR') return null;
    if (period === 'annual') return amount / LAKH;
    if (period === 'monthly') return amount * 12 / LAKH;
    return null;
  }

  private getNaukriBandFilter(rawText: string | null | undefined): SalaryLpaFilter | null {
    const normalized = this.removeMultipleSpaces(rawText)
      ?.toLowerCase()
      .replace(/\s*-\s*/g, ' - ');

    return normalized ? NAUKRI_BAND_FILTERS[normalized] : null;
  }

  private isLpaInFilter(value: number, filter: SalaryLpaFilter): boolean {
    if (filter === '40+') return value >= 40;

    const match = /^(\d+)-(\d+)$/.exec(filter);
    if (!match) return false;

    const minimum = Number(match[1]);
    const maximum = Number(match[2]);
    return value >= minimum && value <= maximum;
  }

  private formatInrAmount(value: number): string {
    if (value >= CRORE) return `${this.removeTrailingZeros(value / CRORE, 1)} Cr`;
    if (value >= LAKH) return `${this.removeTrailingZeros(value / LAKH, 1)} L`;
    return value.toLocaleString('en-IN');
  }

  private formatInternationalAmount(value: number): string {
    if (value >= 1_000_000) return `${this.removeTrailingZeros(value / 1_000_000, 1)}M`;
    if (value >= 10_000) return `${this.removeTrailingZeros(value / 1_000, 0)}K`;
    return value.toLocaleString('en-US');
  }

  private removeTrailingZeros(value: number, maximumDecimals: number): string {
    return parseFloat(value.toFixed(maximumDecimals)).toString();
  }

  private getPositiveAmount(value: number | null | undefined): number | null {
    const amount = Number(value);
    return value !== null && value !== undefined && Number.isFinite(amount) && amount > 0 ? amount : null;
  }

  private removeMultipleSpaces(value: string | null | undefined): string | null {
    const text = value?.trim();
    return text ? text.replace(/\s+/g, ' ') : null;
  }
}
